import type { MobileAuthenticationResult } from "../../backend/utils/mobile-authentication";

export interface ClientBuildInfo extends Record<string, string> {
  app_version: string;
  build_number: string;
}

export interface NativeClientBuildInfoInput {
  appVersion?: unknown;
  buildNumber?: unknown;
  supportScreenCaptureBridgeVersion?: unknown;
  supportScreenRecordingBridgeVersion?: unknown;
  // The app turns its status bar for a dark page (src/theme/jungleColorTheme.ts)
  colorThemeBridgeVersion?: unknown;
  interfaceLanguageBridgeVersion?: unknown;
}

interface SentryEventLike {
  tags?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown> | undefined>;
}

declare global {
  interface Window {
    __JUNGLE_NATIVE_APP_BUILD_INFO__?: NativeClientBuildInfoInput;
  }
}

const UNKNOWN_BUILD_VALUE = "unknown";
const MAX_BUILD_VALUE_LENGTH = 64;
const SAFE_BUILD_VALUE_PATTERN = /^[a-zA-Z0-9._+-]+$/;

let nativeClientBuildInfoOverride: ClientBuildInfo | null = null;

function normalizeBuildValue(value: unknown): string {
  if (typeof value !== "string") return UNKNOWN_BUILD_VALUE;

  const normalizedValue = value.trim();
  if (
    normalizedValue.length === 0 ||
    normalizedValue.length > MAX_BUILD_VALUE_LENGTH ||
    !SAFE_BUILD_VALUE_PATTERN.test(normalizedValue)
  ) {
    return UNKNOWN_BUILD_VALUE;
  }
  return normalizedValue;
}

function getWebBuildInfo(): ClientBuildInfo {
  return {
    app_version: normalizeBuildValue(import.meta.env.VITE_APP_VERSION),
    build_number: normalizeBuildValue(import.meta.env.VITE_GIT_SHA),
  };
}

function normalizeNativeClientBuildInfo(
  input: NativeClientBuildInfoInput,
  fallbackBuildInfo: ClientBuildInfo | null = null
): ClientBuildInfo {
  return {
    app_version:
      input.appVersion === undefined
        ? (fallbackBuildInfo?.app_version ?? UNKNOWN_BUILD_VALUE)
        : normalizeBuildValue(input.appVersion),
    build_number:
      input.buildNumber === undefined
        ? (fallbackBuildInfo?.build_number ?? UNKNOWN_BUILD_VALUE)
        : normalizeBuildValue(input.buildNumber),
  };
}

function getInjectedNativeClientBuildInfo(): ClientBuildInfo | null {
  if (typeof window === "undefined" || !window.__JUNGLE_NATIVE_APP_BUILD_INFO__) return null;

  const normalizedBuildInfo = normalizeNativeClientBuildInfo(window.__JUNGLE_NATIVE_APP_BUILD_INFO__);
  if (
    normalizedBuildInfo.app_version === UNKNOWN_BUILD_VALUE &&
    normalizedBuildInfo.build_number === UNKNOWN_BUILD_VALUE
  ) {
    return null;
  }
  return normalizedBuildInfo;
}

export function getClientBuildInfo(): ClientBuildInfo {
  if (nativeClientBuildInfoOverride) return nativeClientBuildInfoOverride;

  return getInjectedNativeClientBuildInfo() ?? getWebBuildInfo();
}

export function setNativeClientBuildInfo(input: NativeClientBuildInfoInput): ClientBuildInfo {
  if (input.appVersion === undefined && input.buildNumber === undefined) return getClientBuildInfo();

  const previousNativeBuildInfo = nativeClientBuildInfoOverride ?? getInjectedNativeClientBuildInfo();
  nativeClientBuildInfoOverride = normalizeNativeClientBuildInfo(input, previousNativeBuildInfo);
  return getClientBuildInfo();
}

export function getNativeClientBuildInfoForCurrentAuthentication(
  authenticationResult: MobileAuthenticationResult
): NativeClientBuildInfoInput | null {
  if (
    authenticationResult.status === "legacy_bridge_rejected" ||
    authenticationResult.status === "authentication_superseded"
  ) {
    return null;
  }

  if (!authenticationResult.nativeAppVersion && !authenticationResult.nativeBuildNumber) return null;
  return {
    appVersion: authenticationResult.nativeAppVersion,
    buildNumber: authenticationResult.nativeBuildNumber,
  };
}

export function parseNativeClientBuildInfoMessage(rawMessage: unknown): NativeClientBuildInfoInput | null {
  if (typeof rawMessage !== "string" || rawMessage.trim().length === 0) return null;

  try {
    const parsedMessage = JSON.parse(rawMessage) as NativeClientBuildInfoInput;
    if (!parsedMessage || typeof parsedMessage !== "object" || Array.isArray(parsedMessage)) return null;
    return parsedMessage;
  } catch {
    return null;
  }
}

export function addClientBuildInfoToSentryEvent<T extends SentryEventLike>(event: T): T {
  const clientBuildInfo = getClientBuildInfo();
  return {
    ...event,
    tags: {
      ...event.tags,
      ...clientBuildInfo,
    },
    contexts: {
      ...event.contexts,
      client_build: {
        type: "client_build",
        ...clientBuildInfo,
      },
    },
  } as T;
}

export function resetNativeClientBuildInfoForTests(): void {
  nativeClientBuildInfoOverride = null;
}
